// The MCP runtime's settings document, as the Toolkits surface composes it
// when an admin adds a toolkit (02-DESIGN/08-toolkits/02-the-mcp-runtime.md
// § the settings document, § `$SRV.INFO`). The runtime's own repository,
// impire-toolkit-mcp, holds no wire file yet on 2026-10-24, so this mirrors
// the design's document key for key; the day the runtime's Go wire lands,
// this file names it and any field that differs is a defect here. The
// document is written whole through the configuration service under the
// name every platform image reads.

import { config, toolkit } from "@impire-io/sdk";

/** DOCUMENT is the one document a runtime reads: `settings`. */
export const DOCUMENT = config.DOCUMENT_SETTINGS;

/** The runtime's provenance beside the contract's signature on its service info. */
export const META_RUNTIME_KIND = "runtime.kind";
export const META_RUNTIME_URL = "runtime.url";
export const META_RUNTIME_UPSTREAM_NAME = "runtime.upstream-name";
export const META_RUNTIME_UPSTREAM_VERSION = "runtime.upstream-version";
export const META_SETTINGS_REVISION = toolkit.META_SETTINGS_REVISION;

/** RUNTIME_KIND_MCP is what the platform's MCP runtime advertises as `runtime.kind`. */
export const RUNTIME_KIND_MCP = "mcp";

/** DeclaredTool is one tool a person-scoped toolkit declares in place of `tools/list`. */
export interface DeclaredTool {
  name: string;
  description?: string;
  "input-schema"?: unknown;
}

/** TokenExchange is the declarative exchange block of an `oauth-refresh` toolkit: this toolkit's own registration at the provider. */
export interface TokenExchange {
  "token-endpoint": string;
  "authorize-endpoint": string;
  "client-id": string;
  /** public | confidential; confidential reads the secret at `workload.<id>.client-secret`. */
  "client-type": toolkit.ClientType;
  scopes?: string[];
}

/** CredentialAttachment decides where the credential lands on the outbound request: exactly one. */
export type CredentialAttachment = { bearer: true } | { header: string } | { query: string };

/** Settings is the document, replaced whole. */
export interface Settings {
  /** `toolkit.name`: `[a-z0-9-]+`, unique in the tenant. */
  name: string;
  scope: toolkit.Scope;
  /** The server; its host is on the registration's egress. */
  url: string;
  "auth-scheme": toolkit.Scheme;
  /** Required when the scheme redeems a token. */
  audience?: string;
  /** Shared and public scopes: the allowlist, or ["*"]; required there. */
  tools?: string[];
  /** Person scope only, required there: replaces `tools/list` at startup. */
  "declared-tools"?: DeclaredTool[];
  /** Required for `oauth-refresh`. */
  "token-exchange"?: TokenExchange;
  "credential-attachment"?: CredentialAttachment;
  "description-overrides"?: Record<string, string>;
  "setup-instructions"?: string;
  /** A Go duration; `60s` when absent. */
  "call-timeout"?: string;
  /** 16 when absent. */
  "max-in-flight"?: number;
}

/** ALL_TOOLS is the explicit allowlist that admits every tool the upstream lists. */
export const ALL_TOOLS = "*";

/** The runtime's defaults, as the design states them. */
export const DEFAULT_CALL_TIMEOUT = "60s";
export const DEFAULT_MAX_IN_FLIGHT = 16;

/**
 * normalize is the runtime's rule for an upstream tool name on the bus:
 * lowercase, `_` to `-`; any other character outside `[a-z0-9-]` is a
 * validation failure the admin resolves with the allowlist
 * (02-the-mcp-runtime.md § subjects and naming).
 */
export function normalize(upstream: string): string {
  return upstream.toLowerCase().replace(/_/g, "-");
}

/** hostOf reads the host of a URL, or undefined for what is not one. */
export function hostOf(url: string): string | undefined {
  try {
    const u = new URL(url);
    if (u.protocol !== "https:" && u.protocol !== "http:") {
      return undefined;
    }
    return u.hostname === "" ? undefined : u.hostname;
  } catch {
    return undefined;
  }
}

/**
 * egressOf is what the registration lets the runtime reach, derived from the
 * document and nothing else: the server's host and, for a redeeming scheme,
 * the token endpoint's — each as the executor's rule, TCP 443
 * (02-the-mcp-runtime.md § what an instance is).
 */
export function egressOf(s: Settings): string[] {
  const hosts = new Set<string>();
  const server = hostOf(s.url);
  if (server !== undefined) {
    hosts.add(server);
  }
  const token = s["token-exchange"]?.["token-endpoint"];
  if (token !== undefined) {
    const h = hostOf(token);
    if (h !== undefined) {
      hosts.add(h);
    }
  }
  return [...hosts].map((h) => `allow@${h}:tcp:443`);
}

/**
 * check runs the runtime's own validation of a document before it is written
 * (02-the-mcp-runtime.md § the settings document), so that a document the
 * runtime would refuse is never stored: the required fields, the scope and
 * scheme compatibility, the exchange block exactly for a redeeming scheme,
 * the two tool shapes never both, and exactly one attachment point. It
 * answers the first failure in the runtime's words, or nothing.
 */
export function check(s: Settings): string | undefined {
  if (!toolkit.validName(s.name)) {
    return "name: a toolkit name is lowercase letters, digits and hyphens";
  }
  if (!toolkit.validScope(s.scope)) {
    return "scope: one of person, tenant or none";
  }
  if (hostOf(s.url) === undefined) {
    return "url: the server's address, https://…";
  }
  if (!toolkit.validScheme(s["auth-scheme"])) {
    return "auth-scheme: one of oauth-refresh, token or none";
  }
  if (!toolkit.compatible(s.scope, s["auth-scheme"])) {
    return `scope: ${s.scope} does not go with ${s["auth-scheme"]}`;
  }
  const redeeming = s["auth-scheme"] === toolkit.Scheme.oauthRefresh;
  const exchange = s["token-exchange"];
  if (redeeming) {
    if (exchange === undefined) {
      return "token-exchange: required for oauth-refresh";
    }
    if (hostOf(exchange["token-endpoint"]) === undefined) {
      return "token-exchange.token-endpoint: the provider's token endpoint, https://…";
    }
    if (hostOf(exchange["authorize-endpoint"]) === undefined) {
      return "token-exchange.authorize-endpoint: the provider's authorization endpoint, https://…";
    }
    if (exchange["client-id"] === "") {
      return "token-exchange.client-id: this toolkit's own registration at the provider";
    }
    if (!toolkit.validClientType(exchange["client-type"])) {
      return "token-exchange.client-type: public or confidential";
    }
    if (s.audience === undefined || s.audience === "") {
      return "audience: required when the scheme redeems a token";
    }
  } else if (exchange !== undefined) {
    return "token-exchange: not allowed unless the scheme is oauth-refresh";
  }
  const declared = s["declared-tools"];
  if (s.scope === toolkit.Scope.person) {
    if (declared === undefined || declared.length === 0) {
      return "declared-tools: required for a person-scoped toolkit, whose upstream lists nothing to an unauthenticated session";
    }
    if (s.tools !== undefined) {
      return "tools: a person-scoped toolkit declares its tools instead";
    }
    for (const t of declared) {
      if (!toolkit.validToolName(normalize(t.name))) {
        return `declared-tools: "${t.name}" does not normalize to a tool name`;
      }
    }
  } else {
    if (s.tools === undefined || s.tools.length === 0) {
      return "tools: the allowlist, or * for every tool, is required for a shared or public toolkit";
    }
    if (declared !== undefined) {
      return "declared-tools: only a person-scoped toolkit declares its tools";
    }
  }
  const attach = s["credential-attachment"];
  if (attach !== undefined) {
    const named = ["bearer", "header", "query"].filter((k) => k in attach);
    if (named.length !== 1) {
      return "credential-attachment: exactly one of bearer, header or query";
    }
  }
  if (s["call-timeout"] !== undefined && !/^\d+(ms|s|m)$/.test(s["call-timeout"])) {
    return "call-timeout: a duration such as 60s";
  }
  if (s["max-in-flight"] !== undefined && (!Number.isInteger(s["max-in-flight"]) || s["max-in-flight"] < 1)) {
    return "max-in-flight: a positive whole number";
  }
  return undefined;
}

/**
 * parseDeclaredTools reads what an admin typed as declared tools: a JSON
 * array of tools, or one name per line for tools without a schema. An Error
 * names what was wrong.
 */
export function parseDeclaredTools(text: string): DeclaredTool[] | Error {
  const t = text.trim();
  if (t === "") {
    return [];
  }
  if (t.startsWith("[")) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(t);
    } catch {
      return new Error("the declared tools are not JSON");
    }
    if (!Array.isArray(parsed)) {
      return new Error("the declared tools are a JSON array");
    }
    const out: DeclaredTool[] = [];
    for (const item of parsed as unknown[]) {
      if (typeof item !== "object" || item === null || !("name" in item) || typeof item.name !== "string") {
        return new Error("each declared tool has a name");
      }
      const tool: DeclaredTool = { name: item.name };
      if ("description" in item && typeof item.description === "string") {
        tool.description = item.description;
      }
      if ("input-schema" in item) {
        tool["input-schema"] = item["input-schema"];
      }
      out.push(tool);
    }
    return out;
  }
  return t
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l !== "")
    .map((name) => ({ name }));
}

/** parseTools reads an allowlist as typed: names separated by commas, spaces or lines; `*` for every tool. */
export function parseTools(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter((t) => t !== "");
}
